import type {LexicalEditor} from 'lexical';
import type {RefObject} from 'react';
import {useLayoutEffect} from 'react';

const OVERFLOW_HEIGHT_EPSILON = 2;

type UseLineClampOverflowMeasureArgs = {
  /** Element that receives `-webkit-line-clamp` / overflow hidden (measure `scrollHeight` vs `clientHeight`). */
  containerRef: RefObject<HTMLElement | null>;
  editor: LexicalEditor;
  /** When false, reports `false` and skips observers. */
  measureEnabled: boolean;
  onOverflowChange?: (isTruncated: boolean) => void;
  displayValue: string | undefined;
  documentSynced: boolean;
  yjsValue: Uint8Array | undefined;
  truncateLines: number | undefined;
  enhancedTableCells: boolean | undefined;
  editable: boolean | undefined;
  expanded: boolean | undefined;
  maxHeight: number | string | undefined;
};

/**
 * Tracks whether a line-clamped block is visually truncated and notifies via `onOverflowChange`.
 * Used with read-only Lexical `RichTextArea` when `truncateLines` applies webkit line clamp on the container.
 */
export function useLineClampOverflowMeasure({
  containerRef,
  editor,
  measureEnabled,
  onOverflowChange,
  displayValue,
  documentSynced,
  yjsValue,
  truncateLines,
  enhancedTableCells,
  editable,
  expanded,
  maxHeight,
}: UseLineClampOverflowMeasureArgs): void {
  useLayoutEffect(() => {
    if (!onOverflowChange) {
      return;
    }

    let lastEmitted: boolean | null = null;
    const emit = (next: boolean) => {
      if (lastEmitted === next) {
        return;
      }
      lastEmitted = next;
      onOverflowChange(next);
    };

    if (!measureEnabled) {
      emit(false);
      return;
    }

    const el = containerRef.current;
    if (!el) {
      emit(false);
      return;
    }

    const measure = () => {
      const node = containerRef.current;
      if (!node) {
        emit(false);
        return;
      }
      emit(node.scrollHeight - node.clientHeight > OVERFLOW_HEIGHT_EPSILON);
    };

    measure();
    const rafId = requestAnimationFrame(measure);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            requestAnimationFrame(measure);
          })
        : null;
    resizeObserver?.observe(el);

    const unregisterUpdate = editor.registerUpdateListener(() => {
      requestAnimationFrame(measure);
    });

    const unregisterRoot = editor.registerRootListener(() => {
      requestAnimationFrame(measure);
    });

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      unregisterUpdate();
      unregisterRoot();
      lastEmitted = null;
      onOverflowChange(false);
    };
  }, [
    containerRef,
    displayValue,
    documentSynced,
    editor,
    editable,
    enhancedTableCells,
    expanded,
    maxHeight,
    measureEnabled,
    onOverflowChange,
    truncateLines,
    yjsValue,
  ]);
}
